import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getCampaigns, createCampaign, apiFetch, getClients, getClientsBySegment } from '../services/api';
import * as XLSX from 'xlsx';
import './Marketing.css';

function Marketing() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('campaigns');
  const [campaigns, setCampaigns] = useState([]);
  const [watiContacts, setWatiContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showContactSelector, setShowContactSelector] = useState(false);
  const formRef = useRef(null);

  const [selectedSegment, setSelectedSegment] = useState('wati_contacts');
  const [situacaoFilter, setSituacaoFilter] = useState('todos');
  const [excelPreview, setExcelPreview] = useState([]);
  const [totalPreview, setTotalPreview] = useState(0);
  const [previewClients, setPreviewClients] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [pendingBills, setPendingBills] = useState([]);
  const [selectedBills, setSelectedBills] = useState([]);
  const [selectedExcelContacts, setSelectedExcelContacts] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    platform: 'Wati',
    target_audience: 'wati_contacts',
    message_template: '',
    status: 'rascunho',
    manual_contacts: [],
    situacao: 'todos',
    excel_contacts: [],
  });

  useEffect(() => {
    loadCampaigns();
    loadWatiContacts();
    loadTemplates();
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const data = await getCampaigns('-created_date', 50);
      setCampaigns(data);
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWatiContacts = async () => {
    try {
      const response = await getClients('payment_type=wati_sync&sort=-created_date&limit=500');
      const contacts = response.data || response || [];
      setWatiContacts(contacts);
    } catch (error) {
      console.error('Erro ao carregar contatos Wati:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      const data = await apiFetch('/api/sync/templates');
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
    }
  };

  const handleTemplateChange = async (templateName) => {
    setFormData({ ...formData, message_template: templateName });
    if (templateName) {
      try {
        const data = await apiFetch(`/api/sync/template/${templateName}`);
        setPreviewTemplate(data);
      } catch (error) {
        console.error('Erro ao carregar pré-visualização:', error);
        setPreviewTemplate(null);
      }
    } else {
      setPreviewTemplate(null);
    }
  };

  const loadPreviewClients = async (segment, situacao) => {
    if (segment === 'excel' || segment === 'manual') {
      setPreviewClients([]);
      return;
    }
    setLoadingPreview(true);
    try {
      const res = await getClientsBySegment(segment, situacao, false);
      setPreviewClients(res.clients || []);
    } catch (error) {
      setPreviewClients([]);
    } finally {
      setLoadingPreview(false);
    }
  };

  const loadPendingBills = async () => {
    if (selectedSegment !== 'asaas') return;
    let statusParam = '';
    if (situacaoFilter === 'pendente') statusParam = '?status=pendente';
    else if (situacaoFilter === 'inadimplente') statusParam = '?status=inadimplente';
    try {
      const data = await apiFetch(`/api/bills/pending${statusParam}`);
      setPendingBills(data);
    } catch (err) {
      console.error('Erro ao carregar boletos pendentes:', err);
    }
  };

  useEffect(() => {
    if (showForm && selectedSegment === 'asaas') loadPendingBills();
  }, [selectedSegment, showForm, situacaoFilter]);

  useEffect(() => {
    if (showForm && selectedSegment !== 'excel' && selectedSegment !== 'manual') {
      loadPreviewClients(selectedSegment, situacaoFilter);
    } else {
      setPreviewClients([]);
    }
  }, [selectedSegment, situacaoFilter, showForm]);

  const updateSegmentPreview = async (segment, situacao) => {
    if (segment === 'excel') { setTotalPreview(selectedExcelContacts.length); return; }
    if (segment === 'manual') { setTotalPreview(selectedContacts.length); return; }
    try {
      const res = await getClientsBySegment(segment, situacao, true);
      setTotalPreview(res.count || 0);
    } catch { setTotalPreview(0); }
  };

  useEffect(() => {
    if (showForm) updateSegmentPreview(selectedSegment, situacaoFilter);
  }, [selectedSegment, situacaoFilter, excelPreview, selectedContacts, selectedExcelContacts, showForm]);

  const handleSegmentChange = (val) => {
    setSelectedSegment(val);
    setFormData(prev => ({ ...prev, target_audience: val }));
    setShowContactSelector(val === 'manual');
    if (val !== 'excel') {
      setExcelPreview([]);
      setFormData(prev => ({ ...prev, excel_contacts: [] }));
    }
    if (val !== 'asaas') setSelectedBills([]);
  };

  const handleSituacaoChange = (val) => {
    setSituacaoFilter(val);
    setFormData(prev => ({ ...prev, situacao: val }));
    if (selectedSegment === 'asaas') loadPendingBills();
  };

 const handleExcelUpload = (e) => {
  const file = e.target.files[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (evt) => {
    const data = new Uint8Array(evt.target.result);
    const workbook = XLSX.read(data, { type: 'array' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
    if (rows.length < 2) return;

    const headers = rows[0].map(cell => cell?.toString().toLowerCase());
    const nameIdx = headers.findIndex(h => h?.includes('nome'));
    const phoneIdx = headers.findIndex(h => h?.includes('telefone') || h?.includes('whatsapp'));
    const cpfIdx = headers.findIndex(h => h?.includes('cpf') || h?.includes('documento'));

    if (nameIdx === -1 || phoneIdx === -1) {
      alert('Planilha deve conter colunas "Nome" e "Telefone"');
      return;
    }
    if (cpfIdx === -1) {
      alert('Atenção: coluna "CPF" não encontrada. Boletos não serão anexados.');
    }

    const contacts = [];
    for (let i = 1; i < rows.length; i++) {
      const name = rows[i][nameIdx]?.toString().trim();
      let phone = rows[i][phoneIdx]?.toString().trim();
      let cpf = cpfIdx !== -1 ? rows[i][cpfIdx]?.toString().trim() : null;
      if (name && phone) {
        phone = phone.replace(/\D/g, '');
        if (cpf) cpf = cpf.replace(/\D/g, ''); // só números
        contacts.push({ name, phone, cpf });
      }
    }
    setExcelPreview(contacts);
    setSelectedExcelContacts(contacts.map(c => c.phone));
    setFormData(prev => ({ ...prev, excel_contacts: contacts }));
  };
  reader.readAsArrayBuffer(file);
};

  const toggleExcelSelectAll = () => {
    if (selectedExcelContacts.length === excelPreview.length) {
      setSelectedExcelContacts([]);
    } else {
      setSelectedExcelContacts(excelPreview.map(c => c.phone));
    }
  };

  const toggleBillSelectAll = () => {
    if (selectedBills.length === pendingBills.length) {
      setSelectedBills([]);
    } else {
      setSelectedBills(pendingBills.map(b => b.id));
    }
  };

  const toggleManualSelectAll = () => {
    if (selectedContacts.length === watiContacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(watiContacts.map(c => c.id));
    }
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    let manualContactsFinal = [];
    let selectedBillsFinal = [];

    if (selectedSegment === 'manual') {
      manualContactsFinal = selectedContacts;
    } else if (selectedSegment === 'excel') {
      manualContactsFinal = excelPreview.filter(c => selectedExcelContacts.includes(c.phone));
    } else if (selectedSegment === 'asaas') {
      selectedBillsFinal = selectedBills;
    }

    const campaignData = {
      ...formData,
      target_audience: selectedSegment,
      situacao: situacaoFilter,
      manual_contacts: manualContactsFinal,
      excel_contacts: selectedSegment === 'excel' ? manualContactsFinal : [],
      selected_bill_ids: selectedBillsFinal,
    };
    try {
      const newCampaign = await createCampaign(campaignData);
      setCampaigns([newCampaign, ...campaigns]);
      setShowForm(false);
      resetForm();
      alert('Campanha criada com sucesso!');
    } catch (error) {
      alert('Erro ao criar campanha: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      platform: 'Wati',
      target_audience: 'wati_contacts',
      message_template: '',
      status: 'rascunho',
      manual_contacts: [],
      situacao: 'todos',
      excel_contacts: [],
    });
    setSelectedSegment('wati_contacts');
    setSituacaoFilter('todos');
    setSelectedContacts([]);
    setExcelPreview([]);
    setSelectedBills([]);
    setSelectedExcelContacts([]);
    setShowContactSelector(false);
    setPreviewTemplate(null);
  };

  const handleSendCampaign = async (campaignId) => {
    if (!window.confirm('Deseja enviar esta campanha agora?')) return;
    setSending(true);
    try {
      const response = await apiFetch(`/api/campaigns/send/${campaignId}`, {
        method: 'POST',
        body: JSON.stringify({ testMode: false }),
      });
      alert(`Campanha enviada! Enviados: ${response.sent}, Erros: ${response.errors}`);
      await loadCampaigns();
    } catch (error) {
      alert('Erro ao enviar campanha: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  const handleTestSend = async (campaignId) => {
    const testPhone = prompt('Digite o número do WhatsApp para teste (com DDD):');
    if (!testPhone) return;
    setSending(true);
    try {
      await apiFetch(`/api/campaigns/send/${campaignId}`, {
        method: 'POST',
        body: JSON.stringify({ testMode: true, testPhone }),
      });
      alert(`Teste enviado para ${testPhone}!`);
    } catch (error) {
      alert('Erro ao enviar teste: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  const openForm = () => {
    setShowForm(true);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      rascunho: { label: 'Rascunho', class: 'status-draft' },
      ativo: { label: 'Ativo', class: 'status-active' },
      enviando: { label: 'Enviando...', class: 'status-sending' },
      finalizado: { label: 'Finalizado', class: 'status-completed' },
      erro: { label: 'Erro', class: 'status-error' },
    };
    const config = statusConfig[status] || statusConfig.rascunho;
    return <span className={`status-badge ${config.class}`}>{config.label}</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
  };

  const activeCampaigns = campaigns.filter(c => c.status === 'ativo' || c.status === 'enviando').length;
  const totalSent = campaigns.reduce((sum, c) => sum + (c.total_sent || 0), 0);
  const totalResponses = campaigns.reduce((sum, c) => sum + (c.total_responses || 0), 0);
  const conversionRate = totalResponses > 0 ? ((totalResponses / totalSent) * 100).toFixed(1) : 0;

  const getTargetLabel = (campaign) => {
    const labels = {
      todos: 'Todos os clientes',
      inadimplentes: 'Inadimplentes',
      em_dia: 'Clientes em dia',
      wati_contacts: 'Contatos WhatsApp',
      cpfl: 'CPFL',
      asaas: 'Asaas',
      pix: 'Pix',
      excel: 'Lista Excel',
      manual: `Seleção manual (${campaign.manual_contacts?.length || 0})`,
    };
    let label = labels[campaign.target_audience] || campaign.target_audience;
    if (campaign.situacao && campaign.situacao !== 'todos') {
      const sit = { adimplente: 'Adimplentes', inadimplente: 'Inadimplentes', pendente: 'Pendentes' };
      label += ` · ${sit[campaign.situacao] || campaign.situacao}`;
    }
    return label;
  };

  return (
    <div className="marketing-page">
      <div className="page-header">
        <div>
          <h1>Marketing</h1>
          <p className="page-subtitle">Campanhas e geração de leads via WhatsApp</p>
        </div>
        <button className="btn-primary" onClick={openForm}>+ Nova Campanha</button>
      </div>

      <div className="marketing-tabs">
        <button
          className={`tab-btn ${activeTab === 'campaigns' ? 'active' : ''}`}
          onClick={() => setActiveTab('campaigns')}
        >
          📢 Campanhas
        </button>
        <button
          className={`tab-btn ${activeTab === 'contacts' ? 'active' : ''}`}
          onClick={() => setActiveTab('contacts')}
        >
          📞 Contatos WhatsApp ({watiContacts.length})
        </button>
      </div>

      {activeTab === 'campaigns' && (
        <>
          <div className="mkt-stats-grid">
            <div className="mkt-stat-card">
              <div className="mkt-stat-label">Campanhas Ativas</div>
              <div className="mkt-stat-value">{activeCampaigns}</div>
            </div>
            <div className="mkt-stat-card">
              <div className="mkt-stat-label">Mensagens Enviadas</div>
              <div className="mkt-stat-value">{totalSent.toLocaleString()}</div>
            </div>
            <div className="mkt-stat-card">
              <div className="mkt-stat-label">Taxa de Resposta</div>
              <div className="mkt-stat-value">{conversionRate}%</div>
            </div>
          </div>

          <div className="campaigns-table-container">
            <h3 className="section-title">Campanhas Recentes</h3>
            {loading ? (
              <div className="loading-state">Carregando campanhas...</div>
            ) : campaigns.length === 0 ? (
              <div className="empty-state">
                <p>Nenhuma campanha criada ainda.</p>
                <button className="btn-primary" onClick={openForm}>Criar primeira campanha</button>
              </div>
            ) : (
              <table className="campaigns-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Público Alvo</th>
                    <th>Template</th>
                    <th>Status</th>
                    <th>Enviados</th>
                    <th>Respostas</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map(campaign => (
                    <tr key={campaign.id}>
                      <td className="campaign-name">{campaign.name}</td>
                      <td>{getTargetLabel(campaign)}</td>
                      <td className="template-preview">{campaign.message_template?.substring(0, 30)}...</td>
                      <td>{getStatusBadge(campaign.status)}</td>
                      <td>{campaign.total_sent || 0}</td>
                      <td>{campaign.total_responses || 0}</td>
                      <td className="campaign-actions">
                        {campaign.status === 'rascunho' && (
                          <>
                            <button className="btn-test" onClick={() => handleTestSend(campaign.id)} disabled={sending}>
                              📱 Teste
                            </button>
                            <button className="btn-send" onClick={() => handleSendCampaign(campaign.id)} disabled={sending}>
                              Enviar
                            </button>
                          </>
                        )}
                        {campaign.status === 'finalizado' && <span className="completed-badge">✓ Enviado</span>}
                        {campaign.status === 'enviando' && <span className="sending-badge">⏳ Enviando...</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {showForm && (
            <div className="campaign-form-inline" ref={formRef}>
              <h3>Nova Campanha</h3>
              <form onSubmit={handleCreateCampaign}>

                {/* Nome */}
                <div className="form-group">
                  <label>Nome da Campanha *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Ex: Campanha Gastro Abril"
                  />
                </div>

                {/* Template */}
                <div className="form-group">
                  <label>Template Wati *</label>
                  <select
                    value={formData.message_template}
                    onChange={e => handleTemplateChange(e.target.value)}
                    required
                  >
                    <option value="">Selecione um template</option>
                    {templates.map(t => (
                      <option key={t.id} value={t.elementName}>
                        {t.elementName} ({t.category})
                      </option>
                    ))}
                  </select>
                  <small>Selecione um template aprovado na Wati</small>
                </div>

                {/* Pré-visualização do template */}
                {previewTemplate && (
                  <div className="template-preview">
                    <h4 style={{ margin: '0 0 10px', fontSize: 13, color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Pré-visualização</h4>
                    {previewTemplate.header?.mediaFromPC && (
                      <img
                        src={`/api/wati/media?fileName=${encodeURIComponent(previewTemplate.header.mediaFromPC)}&mediaId=${previewTemplate.header.mediaHeaderId}`}
                        alt="Header do template"
                        style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', marginBottom: 10 }}
                        onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }}
                      />
                    )}
                    <div className="preview-body">
                      {previewTemplate.body?.split('\n').map((line, i) => <p key={i}>{line}</p>)}
                    </div>
                    {previewTemplate.buttons?.length > 0 && (
                      <div className="preview-buttons">
                        {previewTemplate.buttons.map((btn, idx) => (
                          <span key={idx} className="preview-button">{btn.parameter?.text}</span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Público Alvo */}
                <div className="form-group">
                  <label>Público Alvo</label>
                  <select value={selectedSegment} onChange={e => handleSegmentChange(e.target.value)}>
                    <option value="wati_contacts">Todos os contatos WhatsApp</option>
                    <option value="cpfl">CPFL</option>
                    <option value="asaas">Asaas</option>
                    <option value="pix">Pix</option>
                    <option value="excel">Lista Excel</option>
                    <option value="manual">Selecionar manualmente...</option>
                  </select>
                </div>

                {/* Filtro de situação */}
                {['cpfl', 'asaas', 'pix'].includes(selectedSegment) && (
                  <div className="form-group">
                    <label>Filtrar por situação</label>
                    <select value={situacaoFilter} onChange={e => handleSituacaoChange(e.target.value)}>
                      <option value="todos">Todos</option>
                      <option value="adimplente">Adimplentes</option>
                      <option value="inadimplente">Inadimplentes</option>
                      <option value="pendente">Pendentes</option>
                    </select>
                  </div>
                )}

                {/* Lista de boletos (Asaas) */}
                {selectedSegment === 'asaas' && pendingBills.length > 0 && (
                  <div className="form-group">
                    <label>Selecione os clientes e o boleto a enviar</label>
                    <div className="selectable-list">
                      <div className="selectable-list-header">
                        <div className="selectable-list-header-left">
                          <input
                            type="checkbox"
                            checked={selectedBills.length === pendingBills.length && pendingBills.length > 0}
                            onChange={toggleBillSelectAll}
                          />
                          <span className="selectable-list-header-label">Selecionar todos</span>
                        </div>
                        <span className="selectable-list-count">{pendingBills.length} boletos</span>
                      </div>
                      <div className="selectable-list-body">
                        {pendingBills.map(bill => (
                          <div
                            key={bill.id}
                            className={`selectable-list-item ${selectedBills.includes(bill.id) ? 'selected' : ''}`}
                            onClick={() => {
                              if (selectedBills.includes(bill.id)) {
                                setSelectedBills(selectedBills.filter(id => id !== bill.id));
                              } else {
                                setSelectedBills([...selectedBills, bill.id]);
                              }
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedBills.includes(bill.id)}
                              onChange={() => {}}
                            />
                            <div className="selectable-item-info">
                              <span className="selectable-item-name">
                                {bill.client?.full_name || bill.client_name}
                              </span>
                              <span className="bill-due-date">
                                Vencimento: {new Date(bill.due_date).toLocaleDateString('pt-BR')}
                              </span>
                            </div>
                            <span className="bill-value">{formatCurrency(bill.value)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="selectable-list-footer">
                        <strong>{selectedBills.length}</strong> de {pendingBills.length} selecionado(s)
                      </div>
                    </div>
                  </div>
                )}

                {/* Preview de clientes (CPFL / Pix / etc.) */}
                {previewClients.length > 0 && (
                  <div className="form-group">
                    <label>Clientes que receberão a campanha</label>
                    <div className="preview-clients-list">
                      {previewClients.slice(0, 10).map(client => (
                        <div key={client.id} className="preview-client-item">
                          <span className="client-name">{client.full_name || client.name}</span>
                          <span className="client-cpf">{client.cpf || client.cpfCnpj || '—'}</span>
                        </div>
                      ))}
                      {previewClients.length > 10 && (
                        <div className="preview-more">... e mais {previewClients.length - 10} cliente(s)</div>
                      )}
                    </div>
                  </div>
                )}
                {loadingPreview && (
                  <div className="form-group" style={{ color: '#64748b', fontSize: 13 }}>Carregando lista...</div>
                )}

                {/* Upload de Excel */}
                {selectedSegment === 'excel' && (
                  <div className="form-group">
                    <label>Arquivo Excel <small style={{ fontWeight: 400, textTransform: 'none', fontSize: 12 }}>(colunas obrigatórias: NOME, TELEFONE)</small></label>
                    <div className="file-upload-box">
                      <input type="file" accept=".xlsx,.xls,.csv" onChange={handleExcelUpload} />
                    </div>

                    {excelPreview.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div className="selectable-list">
                          <div className="selectable-list-header">
                            <div className="selectable-list-header-left">
                              <input
                                type="checkbox"
                                checked={selectedExcelContacts.length === excelPreview.length}
                                onChange={toggleExcelSelectAll}
                              />
                              <span className="selectable-list-header-label">Selecionar todos</span>
                            </div>
                            <span className="selectable-list-count">{excelPreview.length} contatos</span>
                          </div>
                          <div className="selectable-list-body">
                            {excelPreview.map(contact => (
                              <div
                                key={contact.phone}
                                className={`selectable-list-item ${selectedExcelContacts.includes(contact.phone) ? 'selected' : ''}`}
                                onClick={() => {
                                  if (selectedExcelContacts.includes(contact.phone)) {
                                    setSelectedExcelContacts(selectedExcelContacts.filter(p => p !== contact.phone));
                                  } else {
                                    setSelectedExcelContacts([...selectedExcelContacts, contact.phone]);
                                  }
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedExcelContacts.includes(contact.phone)}
                                  onChange={() => {}}
                                />
                                <div className="selectable-item-info">
                                  <span className="selectable-item-name">{contact.name}</span>
                                  <span className="selectable-item-detail">{contact.phone}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="selectable-list-footer">
                            <strong>{selectedExcelContacts.length}</strong> de {excelPreview.length} selecionado(s)
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Seleção manual de contatos */}
                {selectedSegment === 'manual' && (
                  <div className="form-group">
                    <label>Selecione os contatos</label>
                    <div className="selectable-list">
                      <div className="selectable-list-header">
                        <div className="selectable-list-header-left">
                          <input
                            type="checkbox"
                            checked={selectedContacts.length === watiContacts.length && watiContacts.length > 0}
                            onChange={toggleManualSelectAll}
                          />
                          <span className="selectable-list-header-label">Selecionar todos</span>
                        </div>
                        <span className="selectable-list-count">{watiContacts.length} contatos</span>
                      </div>
                      <div className="selectable-list-body">
                        {watiContacts.map(contact => (
                          <div
                            key={contact.id}
                            className={`selectable-list-item ${selectedContacts.includes(contact.id) ? 'selected' : ''}`}
                            onClick={() => {
                              if (selectedContacts.includes(contact.id)) {
                                setSelectedContacts(selectedContacts.filter(id => id !== contact.id));
                              } else {
                                setSelectedContacts([...selectedContacts, contact.id]);
                              }
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedContacts.includes(contact.id)}
                              onChange={() => {}}
                            />
                            <div className="selectable-item-info">
                              <span className="selectable-item-name">{contact.full_name || contact.name || '—'}</span>
                              <span className="selectable-item-detail">{contact.phone}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="selectable-list-footer">
                        <strong>{selectedContacts.length}</strong> de {watiContacts.length} selecionado(s)
                      </div>
                    </div>
                  </div>
                )}

                {/* Resumo de destinatários */}
                <div className="form-group">
                  <label>Destinatários estimados</label>
                  <div className="recipients-summary">
                    <strong>{totalPreview}</strong>
                    <span>cliente(s) receberão esta campanha</span>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-cancel" onClick={() => setShowForm(false)}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary">
                    Criar Campanha
                  </button>
                </div>
              </form>
            </div>
          )}
        </>
      )}

      {activeTab === 'contacts' && (
        <div className="contacts-container">
          <div className="contacts-header">
            <h3>📱 Contatos sincronizados do WhatsApp</h3>
            <p>Esses contatos entraram em contato pelo WhatsApp e ainda não são clientes pagantes.</p>
          </div>
          {watiContacts.length === 0 ? (
            <div className="empty-state">
              <p>Nenhum contato sincronizado ainda.</p>
              <button
                className="btn-primary"
                onClick={async () => {
                  await apiFetch('/api/sync/contacts', { method: 'POST' });
                  loadWatiContacts();
                }}
              >
                Sincronizar agora
              </button>
            </div>
          ) : (
            <div className="contacts-table-wrapper">
              <table className="contacts-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Telefone</th>
                    <th>Data de criação</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {watiContacts.map(contact => (
                    <tr key={contact.id}>
                      <td className="contact-name">{contact.full_name || contact.name || '—'}</td>
                      <td>{contact.phone}</td>
                      <td>{formatDate(contact.created_date)}</td>
                      <td>
                        <button
                          className="btn-small"
                          onClick={() => alert(`Em breve: enviar mensagem para ${contact.phone}`)}
                        >
                          Enviar mensagem
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Marketing;